<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { usePatientFromRoute } from '@/composables/usePatientFromRoute';
import { getParam } from '@/lib/utils';
import { isInPerson } from '@/store/features';
import { ArrowRight } from 'lucide-vue-next';
import { onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const router = useRouter();
const route = useRoute();
const { patientId } = usePatientFromRoute();

onMounted(() => {
    // if this is a reschedule, skip consent and go directly to book_appointment
    if (localStorage.getItem('reschedule')) {
        router.replace({ 
            name: 'book_appointment', 
            params: { domain: getParam(route), patientId: patientId.value } 
        });
    }
});

watch(isInPerson, (val) => {
    // skip the whole thing if appointment is for an in-person clinic
    if (val) {
        router.replace({ name: 'book_appointment', params: { domain: getParam(route), patientId: patientId.value } });
    }
}, { immediate: true });

function handleContinue() {
    router.push({ 
        name: 'book_appointment', 
        params: { domain: getParam(route), patientId: patientId.value } 
    });
}

const conditions = [
    {
        title: 'Emergency conditions',
        description: 'If you have chest pain, severe shortness of breath, major injury, or suicidal thoughts, call 911 or go to the nearest emergency room.',
    },
    {
        title: 'Narcotic medications',
        description: 'We are unable to prescribe narcotic or opioid medications in virtual visits.',
    },
    {
        title: 'WorkSafeBC or ICBC injuries',
        description: 'These claims must be handled in person. Please book a clinic appointment.',
    },
    {
        title: 'Multiple concerns',
        description: 'For the best care and use of your appointment time, we recommend discussing your most important concern. We may address the rest in future appointments.',
    }
];
</script>

<template>
    <div class="flex flex-col gap-10 px-4 pb-10 md:gap-14 md:pt-10">
        <h1 class="text-3xl font-bold">Please make sure your visit is NOT for any of the following:</h1>
        
        <div class="space-y-4">
            <div v-for="(condition, index) in conditions" :key="index" class="flex gap-3">
                <div class="mt-1.5 w-3 h-3 rounded-full bg-secondary shrink-0"></div>
                <div class="space-y-1">
                    <h3 class="font-bold text-gray-900">{{ condition.title }}</h3>
                    <p class="text-gray-600 text-sm leading-relaxed font-medium">{{ condition.description }}</p>
                </div>
            </div>
        </div>
        
        <Button 
            @click="handleContinue" 
            size="lg" 
            class="w-full"
        >
            I understand. Continue <ArrowRight class="w-6 h-6 ml-1" />
        </Button>
    </div>
</template>